import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Square, MapPin, Plus, CheckCircle2, Lock, Users, TicketPercent } from "lucide-react";
import { fmtUsd } from "@/lib/format";
import TripRecorder from "@/components/TripRecorder";
import StartEndPicker from "@/components/StartEndPicker";
import NavigateButton, { openNavigation } from "@/components/NavigateButton";
import TripMap from "@/components/TripMap";
import SlideToStart from "@/components/SlideToStart";
import PassengerPicker from "@/components/PassengerPicker";
import TripCloseoutDialog from "@/components/TripCloseoutDialog";
import { useConfig } from "@/contexts/ConfigContext";

// v2.6.9 batch BF-1 · Default rates kept for legacy fallback.
// v2.6.9 batch BT · Multi-rider flat: when pax ≥ threshold, replace base_fare
// with `multi_rider_flat_per_pax × passengers`. All other variable fees stack.
const DEFAULT_RATES = {
  base_fare: 5, per_mile: 1.25, per_minute: 0.30,
  per_stop_per_minute: 1.00, per_passenger: 0,
  multi_rider_flat_per_pax: 8.0, multi_rider_threshold: 2,
};

// Auto-wait constants for Weatherford variant — below 1 MPH for 30s triggers
// wait mode, charging $/STOP-MIN per minute until the vehicle starts moving again.
const WAIT_SPEED_MS = 0.447;  // 1 MPH in m/s
const WAIT_DWELL_MS = 30000;  // 30-second dwell before wait mode engages

/**
 * v2.6.9 batch BF-1 · Live Meter w/ variant.
 *   variant="statewide"   → original full-rates meter (default)
 *   variant="weatherford" → locked rates ($5 base, $0 mile, $0 min, $1 stop-min),
 *     auto-wait detection enabled (below 1 MPH for ≥30s triggers $/STOP-MIN).
 *
 * Rates pulled from /api/config → presets.meter_rates.<variant> so admins can
 * edit both meter presets in Admin → Prices.
 */
export default function MeterPage({ variant = "statewide" } = {}) {
  const { config, reload: reloadConfig } = useConfig();
  const isWeatherford = variant === "weatherford";
  const meterTitle = isWeatherford
    ? "Live Meter — Weatherford City Limits"
    : "Live Meter — Statewide";

  // v2.6.9 batch BF-3 · Always fetch fresh rates when the driver opens the
  // meter so admin edits in Pricing show up immediately (no need to reload
  // the whole app or hard-refresh the page).
  useEffect(() => { reloadConfig?.(); }, [reloadConfig]);

  // v2.6.9 batch BE-2 · TAXIquote handoff — when the driver clicks "START TRIP"
  // on TAXIquote we navigate here with the quoted miles/minutes/payment_method
  // pre-loaded. Banner shows the quoted total for reference; GPS still tracks
  // actual distance so the closeout reflects real numbers.
  // (GPS-only — Manual mode was removed in batch BE-2 per spec.)
  const [searchParams] = useSearchParams();
  const [quotedTotal, setQuotedTotal] = useState(null);
  const [quotedFrom, setQuotedFrom] = useState(null);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [pax, setPax] = useState(1);
  const [waiting, setWaiting] = useState(false);
  const [stops, setStops] = useState(0);

  // Pull variant-specific rates from server config when it arrives. For
  // Weatherford the rate fields are LOCKED (admin-editable only); the driver
  // sees them but cannot change. Statewide keeps the editable inputs.
  useEffect(() => {
    if (!config?.presets) return;
    const r = config.presets?.meter_rates?.[variant];
    if (r) {
      setRates({
        base_fare: r.base_fare ?? DEFAULT_RATES.base_fare,
        per_mile: r.per_mile ?? DEFAULT_RATES.per_mile,
        per_minute: r.per_minute ?? DEFAULT_RATES.per_minute,
        per_stop_per_minute: r.per_stop_per_minute ?? DEFAULT_RATES.per_stop_per_minute,
        per_passenger: r.per_passenger ?? DEFAULT_RATES.per_passenger,
        multi_rider_flat_per_pax: r.multi_rider_flat_per_pax ?? DEFAULT_RATES.multi_rider_flat_per_pax,
        multi_rider_threshold: r.multi_rider_threshold ?? DEFAULT_RATES.multi_rider_threshold,
      });
    }
  }, [config, variant]);

  const [seconds, setSeconds] = useState(0);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [miles, setMiles] = useState(0);
  const [airport, setAirport] = useState(false);
  const [toll, setToll] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  // Pickup-wait timer (5-min grace, then $5 fee)
  const [pickupWaitSec, setPickupWaitSec] = useState(0);
  const [pickupTimerOn, setPickupTimerOn] = useState(false);
  const [pickupFeeApplied, setPickupFeeApplied] = useState(false);
  const [livePos, setLivePos] = useState(null);
  // v2.6.9 batch BT · "tap and go" — Customer Info card, payment method
  // toggle, and CC surcharge feature are HIDDEN from the driver flow.
  // Payment method always defaults to cash; the closeout dialog (which
  // belongs to admin/dispatcher views) still lets staff record card vs cash
  // for accounting if needed via the closeout submit payload.
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [closeoutFare, setCloseoutFare] = useState(null);
  const [saving, setSaving] = useState(false);

  // v2.7.58+E39 · Driver Meter Discount System — pulls the active discount
  // catalogue from /api/discounts/active on mount so the "Apply Discount"
  // dropdown always reflects the admin-managed list. Selection applies the
  // deduction to the SUBTOTAL BEFORE tip/CC surcharge (per spec).
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  useEffect(() => {
    let mounted = true;
    api.get("/discounts/active").then((r) => {
      if (mounted) setDiscounts(r.data?.items || []);
    }).catch(() => { /* silent — dropdown just shows "no discounts" */ });
    return () => { mounted = false; };
  }, []);

  // pickup-wait timer effect
  useEffect(() => {
    if (!pickupTimerOn) return;
    const t = setInterval(() => setPickupWaitSec((s) => {
      const next = s + 1;
      if (next >= 300 && !pickupFeeApplied) setPickupFeeApplied(true);
      return next;
    }), 1000);
    return () => clearInterval(t);
  }, [pickupTimerOn, pickupFeeApplied]);

  // v2.6.9 batch BE-2 · Consume TAXIquote → Meter handoff URL params once on mount.
  // Pre-fills addresses, surfaces the quoted total as a banner, primes pax + pay method.
  // v2.6.9 batch CQ · ALSO pulls rider name/phone/email + trip_id from the
  // driver dashboard "Start Trip" handoff so the meter knows whose ride
  // this is. The rider chip below the meter shows it to the driver and the
  // closeout dialog pre-fills the customer details.
  const [activeRider, setActiveRider] = useState(null);
  useEffect(() => {
    if (!searchParams || searchParams.toString() === "") return;
    const so = searchParams.get("origin"); if (so) setOrigin(so);
    const sd = searchParams.get("destination"); if (sd) setDestination(sd);
    const sp = searchParams.get("passengers"); if (sp) setPax(parseInt(sp, 10) || 1);
    const sq = searchParams.get("quote_total"); if (sq) { setQuotedTotal(parseFloat(sq)); setQuotedFrom("TAXIquote"); }
    const cn = searchParams.get("customer_name");
    const cp = searchParams.get("customer_phone");
    const ce = searchParams.get("customer_email");
    const tid = searchParams.get("trip_id");
    if (cn || cp || ce || tid) {
      setActiveRider({ name: cn || "", phone: cp || "", email: ce || "", trip_id: tid || "" });
    }
    // Quiet toast once
    if (sq) toast.success(`TAXIquote handoff · $${parseFloat(sq).toFixed(2)} target`);
    if (cn || cp) toast.success(`Rider loaded · ${cn || ""}${cp ? ` · ${cp}` : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchIdRef = useRef(null);
  const lastPosRef = useRef(null);
  const tickRef = useRef(null);
  // v2.6.9 batch BF-1 · Weatherford auto-wait: timestamp of the most recent
  // moment the vehicle was observed at ≥1 MPH. If `now − fastMoveTsRef > 30s`
  // AND running AND variant==="weatherford" → we engage wait mode automatically.
  const fastMoveTsRef = useRef(Date.now());
  // v2.7.56+E33 · Anti-freeze scaffolding.
  // 1. lastTickMsRef — wall-clock timestamp of the previous ticker fire so we
  //    can CATCH UP if the mobile browser suspended setInterval (iOS Safari,
  //    Chrome Android). Without this, backgrounding the tab for 90s freezes
  //    the meter at whatever second it was on.
  // 2. gpsLastFireRef — wall-clock of the last GPS event; the watchdog
  //    restarts navigator.geolocation.watchPosition if it hasn't fired for
  //    ≥30s so an orphaned watcher can't silently starve the meter.
  // 3. gpsRestartTickRef — a bumped counter so the GPS-watcher useEffect
  //    re-runs on watchdog restart requests.
  const lastTickMsRef = useRef(Date.now());
  const gpsLastFireRef = useRef(Date.now());
  const [gpsRestartTick, setGpsRestartTick] = useState(0);

  // ticker — v2.7.56+E33 · Timestamp-catch-up so mobile tab suspension
  // (screen lock, other app in foreground) doesn't freeze the meter.
  // When the tab wakes up, setInterval fires once with a large deltaMs;
  // we advance seconds by the full elapsed count rather than +1.
  useEffect(() => {
    if (running && !paused) {
      lastTickMsRef.current = Date.now();
      tickRef.current = setInterval(() => {
        const now = Date.now();
        const deltaSec = Math.max(1, Math.round((now - lastTickMsRef.current) / 1000));
        lastTickMsRef.current = now;
        setSeconds(s => s + deltaSec);
        if (waiting) setWaitSeconds(w => w + deltaSec);
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [running, paused, waiting]);

  // v2.7.56+E33 · Tab-visibility catch-up. When the tab is backgrounded
  // and returns to focus, setInterval may have been throttled to 1Hz →
  // 1/60Hz. This handler catches up immediately using wall-clock delta
  // the moment the tab is visible again — no waiting for the next tick.
  useEffect(() => {
    if (!running) return;
    const catchUp = () => {
      if (paused || document.visibilityState !== "visible") return;
      const now = Date.now();
      const deltaSec = Math.floor((now - lastTickMsRef.current) / 1000);
      if (deltaSec > 0) {
        lastTickMsRef.current = now;
        setSeconds(s => s + deltaSec);
        if (waiting) setWaitSeconds(w => w + deltaSec);
      }
    };
    document.addEventListener("visibilitychange", catchUp);
    window.addEventListener("focus", catchUp);
    window.addEventListener("pageshow", catchUp);
    return () => {
      document.removeEventListener("visibilitychange", catchUp);
      window.removeEventListener("focus", catchUp);
      window.removeEventListener("pageshow", catchUp);
    };
  }, [running, paused, waiting]);

  // v2.7.56+E33 · GPS watchdog. Some Android browsers silently kill
  // watchPosition when the device throttles background listeners. We
  // rebuild the watcher if no GPS event has fired for ≥30s.
  useEffect(() => {
    if (!running || paused) return;
    const check = setInterval(() => {
      if (Date.now() - gpsLastFireRef.current > 30_000) {
        console.warn("[meter] GPS watchdog · no fix for 30s · rebuilding watcher");
        setGpsRestartTick(t => t + 1);
        gpsLastFireRef.current = Date.now();
      }
    }, 10_000);
    return () => clearInterval(check);
  }, [running, paused]);

  // v2.7.56+E33 · LocalStorage persistence so a page reload / accidental
  // navigate-away doesn't wipe an active trip. Cached state expires after
  // 12h; restored on next mount if the driver returns to the meter.
  useEffect(() => {
    if (!running) {
      try { localStorage.removeItem("rtg_meter_state"); } catch { /* noop */ }
      return;
    }
    try {
      localStorage.setItem("rtg_meter_state", JSON.stringify({
        variant, seconds, waitSeconds, miles, stops, pax, airport, toll,
        origin, destination, paused, waiting,
        pickupWaitSec, pickupFeeApplied, activeRider,
        saved_at: Date.now(),
      }));
    } catch { /* quota / disabled — soft-fail */ }
  }, [running, seconds, waitSeconds, miles, stops, pax, airport, toll, paused, waiting, variant, origin, destination, pickupWaitSec, pickupFeeApplied, activeRider]);

  // v2.7.56+E33 · Restore-on-mount. If a driver was mid-trip and the app
  // reloaded (crash, refresh, kill-and-relaunch), we offer to resume with
  // the elapsed wall-clock time added to the frozen counter.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("rtg_meter_state");
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s || s.variant !== variant) return;
      const age = Date.now() - (s.saved_at || 0);
      if (age > 12 * 3600_000) {
        localStorage.removeItem("rtg_meter_state");
        return;
      }
      const dtSec = Math.floor(age / 1000);
      if (window.confirm(`Resume meter trip in progress?\n\nElapsed since last save: ${dtSec}s\nDistance so far: ${(s.miles ?? 0).toFixed(2)} mi\n\nClick OK to restore and continue metering, Cancel to discard.`)) {
        setSeconds((s.seconds || 0) + (s.paused ? 0 : dtSec));
        setWaitSeconds((s.waitSeconds || 0) + (s.waiting ? dtSec : 0));
        setMiles(s.miles || 0);
        setStops(s.stops || 0);
        setPax(s.pax || 1);
        setAirport(!!s.airport);
        setToll(!!s.toll);
        if (s.origin) setOrigin(s.origin);
        if (s.destination) setDestination(s.destination);
        setPickupWaitSec(s.pickupWaitSec || 0);
        setPickupFeeApplied(!!s.pickupFeeApplied);
        if (s.activeRider) setActiveRider(s.activeRider);
        setPaused(!!s.paused);
        setWaiting(!!s.waiting);
        setRunning(true);
        lastTickMsRef.current = Date.now();
        gpsLastFireRef.current = Date.now();
        toast.success(`Meter restored · +${dtSec}s carry-over`);
      } else {
        localStorage.removeItem("rtg_meter_state");
      }
    } catch {
      try { localStorage.removeItem("rtg_meter_state"); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GPS watcher (covers distance accumulation AND, for Weatherford, auto-wait detection)
  // v2.7.56+E33 · Depends on `gpsRestartTick` so the watchdog can force a rebuild.
  useEffect(() => {
    if (running && !paused && navigator.geolocation) {
      fastMoveTsRef.current = Date.now(); // reset on start
      gpsLastFireRef.current = Date.now(); // reset watchdog on rebuild
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          gpsLastFireRef.current = Date.now();  // watchdog heartbeat
          const cur = { lat: pos.coords.latitude, lng: pos.coords.longitude, t: Date.now() };
          const speed = pos.coords.speed; // m/s · null if not supported
          if (lastPosRef.current) {
            const d = haversineMiles(lastPosRef.current, cur);
            // ignore tiny GPS noise jitter
            if (d < 0.5 && (speed === null || speed > 0.5)) {
              setMiles(m => m + d);
            }
          }
          lastPosRef.current = cur;
          // Auto-wait detection (Weatherford only). If the device CAN report
          // speed (Android Chrome / iOS Safari do), use that. If not, fall
          // back to inferring speed from the haversine delta over the GPS
          // tick interval.
          if (isWeatherford && speed !== null && !isNaN(speed)) {
            if (speed >= WAIT_SPEED_MS) {
              fastMoveTsRef.current = Date.now();
              if (waiting) setWaiting(false); // resumed
            } else {
              const stoppedFor = Date.now() - fastMoveTsRef.current;
              if (stoppedFor >= WAIT_DWELL_MS && !waiting) setWaiting(true);
            }
          }
        },
        (err) => {
          console.warn("[meter] GPS error:", err?.message || err);
          // Don't toast on every intermittent GPS error — the watchdog will
          // rebuild the watcher automatically if events stop arriving.
        },
        { enableHighAccuracy: true, maximumAge: 1000 }
      );
    }
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, paused, isWeatherford, gpsRestartTick]);

  const start = () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    setRunning(true); setPaused(false); lastPosRef.current = null;
    setMiles(0);
    // v2.6.9 batch BY · City Taxi navigation launcher — auto-open the
    // driver's preferred nav app (Google/Apple, respects rtg_map_pref) the
    // moment the meter starts so the driver doesn't have to tap Navigate
    // separately. Skipped when destination is empty (City meter often
    // starts before destination is known); the manual NavigateButton in
    // the trip summary card remains for that flow.
    if (destination) {
      try { openNavigation(destination, origin || null); }
      catch (e) { console.warn("[meter] nav-launch failed:", e?.message || e); }
    }
  };
  const togglePause = () => setPaused(p => !p);
  const addStop = () => { setStops(s => s + 1); setWaiting(true); toast.success("Stop started"); };
  const endWait = () => { setWaiting(false); toast.success("Resumed"); };

  const useMiles = miles;
  const minutes = (seconds - waitSeconds) / 60;
  const stopMinutes = waitSeconds / 60;

  // v2.6.9 batch BT · Multi-rider logic — when passenger count meets the
  // configured threshold (default ≥2), the BASE fare is replaced with
  // `multi_rider_flat_per_pax × passengers` ($16 for 2, $24 for 3 at the
  // $8 default). All other variable fees (per-mile, per-minute, taxes,
  // tolls, airport) stack on top exactly as before.
  const multiRiderEngaged = pax >= (rates.multi_rider_threshold || 2);
  const effectiveBase = multiRiderEngaged
    ? (rates.multi_rider_flat_per_pax || 0) * pax
    : rates.base_fare;

  const subtotal =
    effectiveBase +
    (rates.per_passenger * pax) +
    (rates.per_mile * useMiles) +
    (rates.per_minute * minutes) +
    (rates.per_stop_per_minute * stopMinutes) +
    (airport ? 15 : 0) +
    (toll ? 25 : 0) +
    (pickupFeeApplied ? 5 : 0) +
    1.55; // app maintenance fee

  // v2.7.58+E39 · Driver-selected discount deducted from the SUBTOTAL before
  // tip and CC surcharge (spec choice a). `discountAmount` is capped at
  // subtotal so a fixed discount larger than the fare doesn't produce a
  // negative net — worst case the trip is free.
  const selectedDiscount = discounts.find((d) => d.id === selectedDiscountId) || null;
  const discountAmount = (() => {
    if (!selectedDiscount || subtotal <= 0) return 0;
    const raw = selectedDiscount.kind === "percent"
      ? subtotal * (Number(selectedDiscount.value) / 100)
      : Number(selectedDiscount.value);
    return Math.max(0, Math.min(subtotal, Number(raw) || 0));
  })();
  const netSubtotal = Math.max(0, subtotal - discountAmount);

  // v2.6.9 batch BT · Credit Card surcharge toggle hidden from the driver
  // meter UI — cc is always 0 here. Card processing is handled at closeout
  // via the physical Square reader, not metered against the driver fare.
  const cc = 0;
  const total = netSubtotal + cc;

  // v2.6.9 batch BT · Broadcast active meter state to the fleet WS (driver
  // role) via the global "rtg:meter_state" event so the dispatch map can
  // render the passenger-count badge while a trip is running. Clear on
  // unmount and whenever the meter is idle. (Effect placed AFTER `total` is
  // declared so the dep array doesn't TDZ on first render.)
  useEffect(() => {
    const detail = running
      ? {
          on_trip: true,
          passengers: pax,
          trip_id: `meter-${variant}-${Date.now().toString(36)}`,
          origin: origin || null,
          destination: destination || null,
          current_fare: total,
        }
      : { on_trip: false, passengers: null };
    window.dispatchEvent(new CustomEvent("rtg:meter_state", { detail }));
  }, [running, pax, origin, destination, total, variant]);

  useEffect(() => () => {
    // On unmount clear so the next page doesn't inherit a stale "on_trip".
    window.dispatchEvent(new CustomEvent("rtg:meter_state", { detail: { on_trip: false, passengers: null } }));
  }, []);

  // End the meter → snapshot fare + open the cash-register closeout dialog.
  // The actual /trips save happens in finalizeTrip after the driver confirms
  // tip / cash tendered / change / rating.
  const stop = () => {
    setRunning(false); setPaused(false); setWaiting(false);
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    if (total <= 0) return;
    // Snapshot a frozen copy of the fare so the dialog total doesn't drift
    // if any input changes underneath while the driver is in the dialog.
    setCloseoutFare({
      miles: parseFloat(useMiles.toFixed(2)),
      minutes: parseFloat(minutes.toFixed(2)),
      stopMinutes: parseFloat(stopMinutes.toFixed(2)),
      subtotal: parseFloat(netSubtotal.toFixed(2)),
      cc: 0,
      total: parseFloat(total.toFixed(2)),
      passengers: pax, stops, airport, toll,
      effective_base: parseFloat(effectiveBase.toFixed(2)),
      multi_rider_engaged: multiRiderEngaged,
      // v2.7.58+E39 · Discount snapshot for audit + backend memo generation.
      discount_id:     selectedDiscount?.id     || null,
      discount_name:   selectedDiscount?.name   || null,
      discount_kind:   selectedDiscount?.kind   || null,
      discount_value:  selectedDiscount ? Number(selectedDiscount.value) : null,
      discount_amount: parseFloat(discountAmount.toFixed(2)) || 0,
    });
    setCloseoutOpen(true);
  };

  const finalizeTrip = async (closeout) => {
    if (!closeoutFare) return;
    setSaving(true);
    try {
      await api.post("/trips", {
        mode: "meter",
        // v2.6.9 batch CQ · Bind this meter close-out to the original
        // dispatch trip + rider so the backend can mark the ride doc
        // completed and surface the rider's name/phone on the receipt
        // without the driver having to retype anything.
        dispatch_trip_id: activeRider?.trip_id || null,
        customer_name:    activeRider?.name || null,
        customer_phone:   activeRider?.phone || null,
        customer_email:   activeRider?.email || null,
        miles: closeoutFare.miles,
        minutes: closeoutFare.minutes,
        passengers: closeoutFare.passengers,
        stops: closeoutFare.stops,
        breakdown: {
          base_fare: closeoutFare.effective_base ?? rates.base_fare,
          multi_rider_engaged: !!closeoutFare.multi_rider_engaged,
          multi_rider_flat_per_pax: rates.multi_rider_flat_per_pax,
          per_passenger: rates.per_passenger, passengers: closeoutFare.passengers,
          per_mile: rates.per_mile, miles: closeoutFare.miles,
          per_minute: rates.per_minute, minutes: closeoutFare.minutes,
          stop_minutes: closeoutFare.stopMinutes, stops: closeoutFare.stops, per_stop_per_minute: rates.per_stop_per_minute,
          airport_fee: closeoutFare.airport ? 15 : 0, toll_fee: closeoutFare.toll ? 25 : 0,
        },
        subtotal: closeoutFare.subtotal,
        cc_surcharge: 0,
        total: closeoutFare.total,
        payment_method: closeout.payment_method,
        tip_amount: closeout.tip_amount,
        cash_tendered: closeout.cash_tendered,
        change_due: closeout.change_due,
        rating: closeout.rating,
        rating_comment: closeout.rating_comment,
        // v2.7.58+E39 · Discount attributes — backend appends an internal
        // memo describing exactly which discount ran and the deducted $.
        discount_id:     closeoutFare.discount_id,
        discount_name:   closeoutFare.discount_name,
        discount_kind:   closeoutFare.discount_kind,
        discount_value:  closeoutFare.discount_value,
        discount_amount: closeoutFare.discount_amount,
      });
      toast.success(`Trip closed · ${fmtUsd(closeoutFare.total + (closeout.tip_amount || 0))} · ${closeout.rating}★`);
      // Reset meter state
      setSeconds(0); setWaitSeconds(0); setMiles(0); setStops(0);
      setAirport(false); setToll(false);
      setCloseoutOpen(false); setCloseoutFare(null);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={meterTitle}>
      {/* v2.6.9 batch CQ · Active rider chip — shows whose ride this is so
          the driver doesn't have to flip back to the dispatch tab. Renders
          only when the meter was opened via the Driver Dashboard "Start
          Trip" handoff (URL params include customer_name/phone). */}
      {activeRider && (activeRider.name || activeRider.phone) && (
        <Card
          className="bg-amber-400/10 border-amber-400/50 p-3 mb-3 flex items-center gap-3"
          data-testid="meter-active-rider"
        >
          <div className="h-10 w-10 rounded-full bg-amber-400 text-zinc-950 flex items-center justify-center font-extrabold text-base flex-shrink-0">
            {(activeRider.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Active Rider {activeRider.trip_id ? `· ${activeRider.trip_id}` : ""}</div>
            <div className="text-sm font-bold text-zinc-100 truncate" data-testid="meter-active-rider-name">{activeRider.name || "(no name)"}</div>
            {activeRider.phone && (
              <a
                href={`tel:${activeRider.phone.replace(/\D/g, "")}`}
                className="text-xs text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline"
                data-testid="meter-active-rider-phone"
              >
                {activeRider.phone}
              </a>
            )}
          </div>
        </Card>
      )}
      {quotedTotal != null && (
        <Card className="bg-emerald-500/10 border-emerald-400/40 p-3 mb-4" data-testid="meter-quote-banner">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">{quotedFrom || "TAXIquote"} HANDOFF · Target Quote</div>
              <div className="text-sm text-zinc-200 truncate">{origin} → {destination}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-emerald-300">Quoted</div>
              <div className="meter-display text-xl font-bold text-emerald-400" data-testid="meter-quoted-total">{fmtUsd(quotedTotal)}</div>
            </div>
          </div>
        </Card>
      )}
      {/* v2.6.9 batch BT · Customer Info card removed from driver meters
          for the "tap and go" flow. Drivers no longer collect rider contact
          info on the meter page. */}

      <Card className="bg-zinc-900 border-zinc-800 p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-zinc-200">Trip Addresses & Navigation</h3>
          <NavigateButton destination={destination} origin={origin} size="sm" testid="meter-navigate-btn"/>
        </div>
        <StartEndPicker origin={origin} setOrigin={setOrigin} destination={destination} setDestination={setDestination} testidPrefix="meter-trip" gpsOnly/>
        <div className="mt-3">
          <TripMap origin={origin} destination={destination} livePosition={livePos} height={260} testid="meter-map"/>
        </div>
        {!running && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-400">
                Pickup wait: <span className="meter-display text-amber-400">{Math.floor(pickupWaitSec/60)}:{String(pickupWaitSec%60).padStart(2,"0")}</span>
                {pickupFeeApplied && <span className="ml-2 text-red-400 font-bold">+$5.00 fee applied</span>}
                {!pickupFeeApplied && pickupTimerOn && <span className="ml-2 text-zinc-500">(grace until 5:00)</span>}
              </div>
              <div className="flex gap-2">
                {!pickupTimerOn ? (
                  <Button data-testid="pickup-arrived-btn" size="sm" onClick={() => setPickupTimerOn(true)} className="h-8 bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-xs border border-amber-400/40">Arrived at pickup</Button>
                ) : (
                  <Button data-testid="pickup-decline-btn" size="sm" variant="outline" onClick={() => { setPickupTimerOn(false); setPickupWaitSec(0); setPickupFeeApplied(false); toast.warning("Ride declined — wait timer reset"); }} className="h-8 border-red-400/40 text-red-400 text-xs">Decline ride</Button>
                )}
              </div>
            </div>

            {/* v2.6.9 batch BT · Driver-side payment method toggle + Card
                banner removed. Driver no longer touches CC in the field —
                use physical Square reader at closeout. Meter defaults to
                cash for accounting; payment method is recorded at closeout. */}

            <SlideToStart
              testid="slide-to-start"
              disabled={!origin || !destination}
              onComplete={() => start()}
              label="SLIDE TO START TRIP"
            />

            {/* v2.6.9 batch BF-1b · Explicit START TRIP button — required because
                the slide-to-start widget can feel awkward on mobile + isn't obvious.
                For the Weatherford meter we relax the address requirement (you know
                where you are; GPS tracks the rest) so the meter can start with
                just the pickup typed or even no address at all. */}
            <Button
              data-testid="meter-start-trip-btn"
              type="button"
              disabled={running || (!isWeatherford && (!origin || !destination))}
              onClick={() => {
                // Soft validate for Weatherford — allow blank addresses (the geofence handles it).
                if (!isWeatherford && (!origin || !destination)) {
                  return toast.error("Enter pickup and drop-off addresses first");
                }
                start();
                toast.success("Meter started · GPS tracking active");
              }}
              className="w-full h-14 mt-2 bg-green-500 hover:bg-green-400 text-zinc-950 font-extrabold tracking-widest text-base"
            >
              <Play className="h-5 w-5 mr-2"/>START TRIP &amp; ENGAGE METER
            </Button>
            {isWeatherford && (!origin || !destination) && (
              <p className="text-[11px] text-amber-300 text-center -mt-1">
                Weatherford meter — addresses optional. GPS tracks the trip automatically.
              </p>
            )}
          </div>
        )}
      </Card>

      <Card className="bg-zinc-950 border-zinc-800 p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`h-2.5 w-2.5 rounded-full ${running && !paused ? "bg-green-400 pulse-dot" : paused ? "bg-yellow-400" : "bg-zinc-600"}`} />
            <span className="text-xs uppercase tracking-widest text-zinc-400">{running ? (paused ? "Paused" : (waiting ? "Waiting" : "Running")) : "Idle"}</span>
          </div>
          <div className="flex items-center gap-3">
            {running && <TripRecorder tripId={`meter-${Date.now()}`} autoStart compact />}
            {/* v2.7.56+E33 · GPS-heartbeat indicator — shows the driver the
                exact seconds since the last GPS event AND the last ticker
                fire. During the anti-freeze beat-test (screen off / app
                backgrounded), watch these numbers stay low → jump → catch
                up when the tab returns. If GPS-age exceeds 30s the
                watchdog kicks in automatically. */}
            {running && !paused && <GpsHeartbeat lastGpsRef={gpsLastFireRef} lastTickRef={lastTickMsRef}/>}
            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold" data-testid="meter-mode">GPS</span>
          </div>
        </div>
        <div className="text-center py-6">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">FARE</div>
          <div className="meter-display text-7xl sm:text-8xl font-extrabold text-amber-400" data-testid="meter-total">{fmtUsd(total)}</div>
          <div className="grid grid-cols-3 gap-3 mt-6 max-w-md mx-auto">
            <Stat label="Miles" value={useMiles.toFixed(2)} testid="meter-miles"/>
            <Stat label="Time" value={fmt(seconds)} testid="meter-time"/>
            <Stat label="Stops" value={stops} testid="meter-stops"/>
          </div>
          {/* v2.7.58+E39 · Apply Discount dropdown. Pulls the active
              catalogue from /api/discounts/active on mount; selection
              deducts from the SUBTOTAL (before tip/CC) in real time. */}
          <div className="mt-6 max-w-md mx-auto text-left" data-testid="meter-discount-block">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <TicketPercent className="h-3 w-3 text-amber-300"/>Apply Discount
              </label>
              {selectedDiscount && discountAmount > 0 && (
                <span className="text-[11px] text-emerald-300 font-bold meter-display" data-testid="meter-discount-amount">
                  −{fmtUsd(discountAmount)}
                </span>
              )}
            </div>
            <select
              value={selectedDiscountId}
              onChange={(e) => setSelectedDiscountId(e.target.value)}
              data-testid="meter-discount-select"
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-400"
            >
              <option value="">— No discount —</option>
              {discounts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} · {d.kind === "percent" ? `${d.value}%` : `$${Number(d.value).toFixed(2)} off`}
                </option>
              ))}
            </select>
            {selectedDiscount && (
              <div className="mt-1.5 text-[10px] text-zinc-500" data-testid="meter-discount-hint">
                Subtotal {fmtUsd(subtotal)} − {selectedDiscount.name} = <span className="text-emerald-300 font-bold meter-display">{fmtUsd(total)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center gap-3 mt-4">
          {running && (
            <>
              <Button data-testid="meter-pause" variant="outline" onClick={togglePause} className="h-14 px-6 bg-zinc-900 border-zinc-700"><Pause className="h-4 w-4 mr-1"/>{paused ? "RESUME" : "PAUSE"}</Button>
              {!waiting ? (
                <Button data-testid="meter-add-stop" variant="outline" onClick={addStop} className="h-14 px-6 bg-zinc-900 border-zinc-700"><Plus className="h-4 w-4 mr-1"/>STOP</Button>
              ) : (
                <Button data-testid="meter-end-wait" variant="outline" onClick={endWait} className="h-14 px-6 bg-zinc-900 border-zinc-700"><MapPin className="h-4 w-4 mr-1"/>CONTINUE RIDE</Button>
              )}
              <Button data-testid="meter-stop" onClick={stop} variant="destructive" className="h-14 px-6"><Square className="h-4 w-4 mr-1"/>END</Button>
            </>
          )}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        {/* v2.6.9 batch BE-2 · GPS-only meter — Manual tab removed per user request. */}
        <div className="mb-3 inline-flex items-center gap-2 rounded-md bg-green-500/10 border border-green-500/40 px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse"/>
          <span className="text-[10px] uppercase tracking-widest text-green-300 font-bold">GPS-Tracked Meter</span>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <Tiny label="Base $" value={rates.base_fare} onChange={(v)=>setRates(r=>({...r, base_fare:v}))} locked={isWeatherford}/>
          <Tiny label="$/Mile" value={rates.per_mile} onChange={(v)=>setRates(r=>({...r, per_mile:v}))} locked={isWeatherford}/>
          <Tiny label="$/Min" value={rates.per_minute} onChange={(v)=>setRates(r=>({...r, per_minute:v}))} locked={isWeatherford}/>
          <Tiny label="$/Pax" value={rates.per_passenger} onChange={(v)=>setRates(r=>({...r, per_passenger:v}))}/>
          <Tiny label="$/Stop-Min" value={rates.per_stop_per_minute} onChange={(v)=>setRates(r=>({...r, per_stop_per_minute:v}))} locked={isWeatherford}/>
          <PassengerPicker value={pax} onChange={setPax} label="Pax" testid="meter-pax" min={1} max={6} className="col-span-1"/>
        </div>

        {/* v2.6.9 batch BT · Multi-rider preview — shows the auto-engaged
            flat fare the moment pax meets the configured threshold. Driver
            sees exactly what the base will become, no surprises. */}
        {multiRiderEngaged && (
          <div className="mt-3 p-3 rounded bg-blue-500/10 border border-blue-400/40 flex items-center gap-2" data-testid="meter-multi-rider-banner">
            <Users className="h-4 w-4 text-blue-300 flex-shrink-0"/>
            <span className="text-xs text-blue-200">
              <span className="font-bold uppercase tracking-wider mr-1">Multi-rider</span>
              {pax} riders · base auto-set to <span className="meter-display font-bold">{fmtUsd(effectiveBase)}</span> (${rates.multi_rider_flat_per_pax}/rider)
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3">
          <ToggleRow label="Airport +$15" checked={airport} onChange={setAirport} testid="meter-airport"/>
          <ToggleRow label="Toll +$25" checked={toll} onChange={setToll} testid="meter-toll"/>
        </div>
      </Card>

      <TripCloseoutDialog
        open={closeoutOpen}
        onOpenChange={setCloseoutOpen}
        subtotal={closeoutFare?.subtotal || 0}
        cc_surcharge={0}
        total={closeoutFare?.total || 0}
        defaultPaymentMethod="cash"
        cardCollected={false}
        onConfirm={finalizeTrip}
        busy={saving}
      />
    </Layout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="meter-display text-xl font-bold mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
function Tiny({ label, value, onChange, locked = false }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}{locked && <Lock className="h-2.5 w-2.5 inline ml-1 -mt-0.5 text-amber-400/70"/>}</Label>
      <div className="relative mt-1">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold">$</span>
        <Input
          type="number"
          step="0.01"
          value={value}
          readOnly={locked}
          onChange={e=>onChange(parseFloat(e.target.value)||0)}
          className={`border-zinc-700 h-10 meter-display pl-5 ${locked ? "bg-zinc-900 text-zinc-500" : "bg-zinc-950"}`}
        />
      </div>
    </div>
  );
}
function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 cursor-pointer">
      <span className="text-xs">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid}/>
    </label>
  );
}
// v2.7.56+E33 · GpsHeartbeat — sub-component that renders how many seconds
// have elapsed since the last GPS event and the last ticker fire. Displayed
// only while the meter is Running + not-Paused. During the mobile beat-test
// (screen locked / app backgrounded) the driver should see:
//   1. tick=0s / gps=0-2s → normal · running · everything green
//   2. Screen off for 30s → tick freezes (browser suspends setInterval),
//      gps counter climbs but stays green up to 30s (watchdog threshold)
//   3. Return to foreground → tick jumps to ~30s → catch-up fires →
//      counter resets to 0 → miles/seconds have advanced by ~30
// If gps stays > 30s the pill turns amber; > 60s → red (watchdog restarted).
function GpsHeartbeat({ lastGpsRef, lastTickRef }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, []);
  const gpsAge  = Math.max(0, Math.floor((now - (lastGpsRef?.current  || now)) / 1000));
  const tickAge = Math.max(0, Math.floor((now - (lastTickRef?.current || now)) / 1000));
  const tone = gpsAge > 60 ? "bg-red-500/15 border-red-400/50 text-red-300"
    : gpsAge > 30 ? "bg-amber-500/15 border-amber-400/50 text-amber-300"
    : "bg-emerald-500/15 border-emerald-400/50 text-emerald-300";
  return (
    <div
      className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest border rounded px-2 py-0.5 ${tone}`}
      title="Wall-clock age of last GPS fix + last ticker fire — should stay ≤2s when running normally. Diagnostic for the anti-freeze fix."
      data-testid="meter-gps-heartbeat"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"/>
      <span data-testid="gps-age">gps {gpsAge}s</span>
      <span className="opacity-60">·</span>
      <span data-testid="tick-age">tick {tickAge}s</span>
    </div>
  );
}
function fmt(s) { const m = Math.floor(s/60), sec = s%60; return `${m}:${String(sec).padStart(2,"0")}`; }
function haversineMiles(a, b) {
  const R = 3958.8;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
  const x = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
